import { useState } from "react";
import {
  Ear,
  Mic,
  Brain,
  Languages,
  Sparkles,
  Gavel,
  Type,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const LAYER_META = {
  understand: { label: "Understand", Icon: Ear, hint: "Detect speech and speakers" },
  transcribe: { label: "Transcribe", Icon: Mic, hint: "Accurate source-language transcript" },
  context: { label: "Understand context", Icon: Brain, hint: "Characters, relationships, scene, emotion" },
  translate: { label: "Translate", Icon: Languages, hint: "Natural, context-aware translation" },
  judge_rewrite: { label: "Judge & rewrite", Icon: Gavel, hint: "Grade every line; only rewrite the flagged ones" },
  // Legacy — kept so jobs generated before the pipeline change still render.
  localize: { label: "Localize", Icon: Sparkles, hint: "Adapt slang, humour, cultural references" },
  subtitle: { label: "Subtitle", Icon: Type, hint: "Line breaks and reading-speed limits" },
  qc: { label: "Quality check", Icon: ShieldCheck, hint: "Timing, grammar, consistency, terminology" },
};

const STATUS_COLORS = {
  done: "text-emerald-400 border-emerald-500/40",
  running: "text-amber-300 border-amber-400/40 animate-pulse",
  pending: "text-zinc-500 border-zinc-700",
  skipped: "text-zinc-500 border-zinc-800",
  error: "text-red-400 border-red-500/40",
};

function StepRow({ step, open, onToggle }) {
  const meta = LAYER_META[step.key] || { label: step.label, Icon: Sparkles, hint: "" };
  const Icon = meta.Icon;
  const status = step.status || "pending";
  const cls = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const canExpand =
    (step.details && Object.keys(step.details).length > 0) || !!step.duration_ms;

  const chars = step.details?.characters || [];
  const notes = step.details?.cultural_notes || [];
  const glossary = step.details?.glossary || [];
  const speakers = step.details?.speakers || [];

  return (
    <div className="border-b border-zinc-800/70 last:border-b-0" data-testid={`engine-step-${step.key}`}>
      <button
        type="button"
        onClick={canExpand ? onToggle : undefined}
        className={`w-full flex items-start gap-4 px-4 md:px-6 py-4 text-left transition-colors duration-150 ${
          canExpand ? "hover:bg-zinc-900/50 cursor-pointer" : "cursor-default"
        }`}
      >
        <div className={`w-9 h-9 shrink-0 border ${cls} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-heading text-sm md:text-base font-semibold tracking-tight text-white truncate">
                {meta.label}
              </div>
              <span className={`text-[10px] font-mono uppercase tracking-widest ${cls.split(" ")[0]}`}>
                {status}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {step.duration_ms > 0 && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {(step.duration_ms / 1000).toFixed(1)}s
                </div>
              )}
              {canExpand &&
                (open ? (
                  <ChevronDown className="w-4 h-4 text-zinc-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-zinc-500" />
                ))}
            </div>
          </div>
          <div className="text-xs md:text-sm text-zinc-400 mt-1 leading-relaxed">
            {step.summary || meta.hint}
          </div>
        </div>
      </button>

      {open && canExpand && (
        <div className="px-4 md:px-6 pb-5 pl-16 md:pl-20 space-y-3">
          {speakers.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                Speakers
              </div>
              <div className="flex flex-wrap gap-1.5">
                {speakers.map((s) => (
                  <span key={s} className="text-xs font-mono border border-zinc-800 px-2 py-0.5 text-zinc-300">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {chars.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                Characters
              </div>
              <ul className="text-xs text-zinc-300 space-y-1">
                {chars.slice(0, 6).map((c, i) => (
                  <li key={`${c.name || "unknown"}-${i}`}>
                    <span className="text-white">{c.name || "—"}</span>
                    {c.role ? <span className="text-zinc-500"> · {c.role}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {notes.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                Cultural notes
              </div>
              <ul className="text-xs text-zinc-300 space-y-1 list-disc pl-4">
                {notes.slice(0, 4).map((n, i) => (
                  <li key={`${i}-${n.slice(0, 20)}`}>{n}</li>
                ))}
              </ul>
            </div>
          )}
          {glossary.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                Preferred terminology
              </div>
              <ul className="text-xs text-zinc-300 space-y-1">
                {glossary.slice(0, 8).map((g, i) => (
                  <li key={`${g.source || "term"}-${i}`}>
                    <span className="text-white">{g.source}</span>
                    <span className="text-zinc-500"> → </span>
                    <span className="text-white">{g.target}</span>
                    {g.note ? <span className="text-zinc-500"> · {g.note}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {step.key === "qc" && step.details && (
            <div className="text-xs text-zinc-400 space-y-1">
              <div>
                <span className="text-zinc-500">Issues flagged:</span>{" "}
                <span className="text-white">{step.details.issues_flagged ?? 0}</span>
              </div>
              <div>
                <span className="text-zinc-500">Terminology fixes:</span>{" "}
                <span className="text-white">{step.details.terminology_fixes ?? 0}</span>
              </div>
              <div className="text-zinc-500">
                Caps · {step.details.cps_cap ?? 17} CPS · {step.details.cpl_cap ?? 42} chars/line
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EnginePipeline({ report, speakers }) {
  const [openKey, setOpenKey] = useState("context");
  const steps = report?.steps || [];
  if (steps.length === 0) return null;

  return (
    <div className="border border-zinc-800 bg-zinc-900/40" data-testid="engine-pipeline">
      <div className="px-4 md:px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-1">
            LinguaSub Engine
          </div>
          <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight text-white">
            7 layers ran on your file
          </h3>
        </div>
        {speakers?.length > 0 && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {speakers.length} speaker{speakers.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>
      <div>
        {steps.map((s) => (
          <StepRow
            key={s.key}
            step={s}
            open={openKey === s.key}
            onToggle={() => setOpenKey(openKey === s.key ? null : s.key)}
          />
        ))}
      </div>
    </div>
  );
}
